import { CoreContainerItem } from '@adobe/aem-core-components-react-spa';
import { Constants } from '@adobe/aem-spa-page-model-manager';
import React, { KeyboardEventHandler, useEffect, useRef } from 'react';
import { ColumnClassNames, getChildComponents } from '../../../../lib/utils/aem/getChildComponents';
import { classes } from '../../../../lib/utils/cssUtils';
import { containersIds, filterIds } from '../../../../lib/tests/testIds';
import FilterPanel from '../FilterPanel/FilterPanel';
import WithDevice from '../../../common/WithDevice/WithDevice';
import { useFiltersView } from '../../../../lib/context/FiltersViewContext';
import styles from './FilteredCardsView.module.scss';
import globalStyles from '../../../../styles/exported.module.scss';
import CourseCardsSection from './CourseCardsSection';
import SummaryRow from './SummaryRow';
import Tabs from '../../compound/Tabs/Tabs';
import { withInternalComponents } from '../../../../lib/context/InternalComponentContext';
import MyAchievementsSection from './MyAchievementsSection';
import RefreshLoader from '../../../common/RefreshLoader/RefreshLoader';
import ErrorInfo from '../ErrorInfo/ErrorInfo';
import { LeftArrow } from '../../../../lib/icons/LeftArrow';
import useMobileExpanded from '../../../../lib/hooks/useMobileExpanded';

const cqContainerKeys = {
  main: 'main',
  noresultssection: 'noresultssection',
} as const;

type ExtendedContainerItem = CoreContainerItem & ColumnClassNames;

const FilteredCardsView = () => {
  const {
    filters,
    isInEditor,
    loading,
    viewType,
    fetchingError,
    tabConfig,
    activeTab,
    handleChangeTab,
    hideTabs,
    ...rest
  } = useFiltersView();
  const mainContainer = rest.cqItems[cqContainerKeys.main] as ExtendedContainerItem;
  const tabsRef = useRef<HTMLLIElement[]>([]);
  const noResultSectionContainer = rest.cqItems[cqContainerKeys.noresultssection] as ExtendedContainerItem;
  const [isMobileTabsExpanded, setIsMobileTabsExpanded, toggleMobileTabsState] = useMobileExpanded(false);

  const getViewCommonComponents = () => (
    <div className={styles.commonComponents}>
      {getChildComponents({
        cqItems: mainContainer[Constants.ITEMS_PROP] ?? {},
        cqItemsOrder: mainContainer[Constants.ITEMS_ORDER_PROP] ?? [],
        cqPath: rest.cqPath,
        isInEditor,
        columnClassNames: mainContainer.columnClassNames ?? {},
      })}
    </div>
  );

  const authoringView = () => (
    <Tabs
      {...rest}
      isInEditor={isInEditor}
      elementsClasses={{ tabNavigation: styles.authoringTabs }}
      items={{
        [cqContainerKeys.main]: {
          bottom: (
            <>
              <SummaryRow />
              <CourseCardsSection />
              <MyAchievementsSection />
            </>
          ),
        },
        [cqContainerKeys.noresultssection]: {
          top: (
            <>
              {getViewCommonComponents()}
              <SummaryRow noresultTab />
            </>
          ),
        },
      }}
    />
  );

  const handleTabKeyDown =
    (index: number): KeyboardEventHandler<HTMLLIElement> =>
    event => {
      const tab = tabConfig?.[index];

      if (tab) {
        if (event.key === 'ArrowLeft') {
          const previousTab = tabConfig?.[index - 1];

          if (previousTab) {
            handleChangeTab?.(previousTab.uniqueId);
            tabsRef.current?.[index - 1]?.focus();
          }
        } else if (event.key === 'ArrowRight') {
          const nextTab = tabConfig?.[index + 1];

          if (nextTab) {
            handleChangeTab?.(nextTab.uniqueId);
            tabsRef.current?.[index + 1]?.focus();
          }
        }
      }
    };

  const publishView = () => (
    <>
      {getViewCommonComponents()}
      <div className={styles.resultsContainer}>
        {loading === 'refresh' && <RefreshLoader />}
        <SummaryRow />
        {filters.totalCount === 0 && loading !== 'initial' ? (
          <>
            {getChildComponents({
              cqItems: noResultSectionContainer[Constants.ITEMS_PROP] ?? {},
              cqItemsOrder: noResultSectionContainer[Constants.ITEMS_ORDER_PROP] ?? [],
              cqPath: rest.cqPath,
              isInEditor,
              columnClassNames: noResultSectionContainer.columnClassNames ?? {},
            })}
          </>
        ) : (
          <>
            <CourseCardsSection />
            <MyAchievementsSection />
          </>
        )}
      </div>
    </>
  );

  useEffect(() => {
    setIsMobileTabsExpanded(false);
  }, [activeTab]);

  return (
    <div
      className={classes(
        'aem-container',
        rest.baseCssClass,
        styles.fullWidthWrapper,
        globalStyles.fullTabsWidth,
        globalStyles.filterPanelComp,
      )}
      data-cmp-is="tabs"
      data-panelcontainer="tabs"
      data-cq-data-path={rest.cqPath}
      data-testid={containersIds.learningContainer}
    >
      <div className={styles.learningGridContainer}>
        <div className={styles.learningGrid}>
          <FilterPanel className={styles.filterPanel} />
          <WithDevice desktop>
            <div className={styles.desktopSpace} />
          </WithDevice>
          <section className={styles.resultsPanel} data-testid={filterIds.resultsList}>
            {!hideTabs && !!tabConfig?.length && (
              <>
                <button className={styles.mobileActiveItemContainer} onClick={toggleMobileTabsState}>
                  <span>{activeTab?.title}</span>
                  <LeftArrow
                    className={classes(styles.mobileArrow, { [styles.mobileArrowExpanded]: isMobileTabsExpanded })}
                  />
                </button>
                <ul
                  className={classes(styles.tabs, { [styles.hiddenMobileTabs]: !isMobileTabsExpanded })}
                  role="tablist"
                >
                  {tabConfig.map((tab, index) => (
                    <li
                      ref={el => {
                        if (el) {
                          tabsRef.current[index] = el;
                        }
                      }}
                      role="tab"
                      tabIndex={activeTab?.uniqueId === tab.uniqueId ? 0 : -1}
                      key={tab.uniqueId}
                      aria-selected={activeTab?.uniqueId === tab.uniqueId}
                      className={classes(styles.tab, { [styles.activeTab]: activeTab?.uniqueId === tab.uniqueId })}
                      onClick={() => handleChangeTab?.(tab.uniqueId)}
                      onKeyDown={handleTabKeyDown(index)}
                    >
                      {tab.title}
                    </li>
                  ))}
                </ul>
              </>
            )}
            {!fetchingError && (isInEditor ? authoringView() : publishView())}
            {!!fetchingError && <ErrorInfo description={fetchingError} />}
          </section>
        </div>
      </div>
    </div>
  );
};

export default withInternalComponents(FilteredCardsView);
